import type { Competitor } from '../models/ProductTypes';
import { SkinSeoulProductType } from '../models/ProductTypes';

export interface MatchedCompetitorProductRequest {
  sku: string;
  possibility: number;
}

export interface MatchedCompetitorProductsRequest {
  competitor: Competitor;
  competitorProductIdentifiers: MatchedCompetitorProductRequest[];
}

export interface SkinSeoulProductIdentifierRequest {
  sku: string;
  type: SkinSeoulProductType;
  variation?: SkinSeoulProductIdentifierRequest[];
}

export interface MatchWithSkinSeoulProductRequest {
  skinSeoulProduct: SkinSeoulProductIdentifierRequest;
  machineMatchedProducts: MatchedCompetitorProductsRequest[];
  humanMatchedProducts?: MatchedCompetitorProductsRequest[];
}

export interface MatchCompetitorResponse {
  success: boolean;
}

export interface CompetitorProductRequest {
  productImageUrls: string[];
  sku: string;
  name: string;
  normalizedName: string;
  brand: string;
  category: string[];
  productLink: string;
  size?: string;
  pack?: string;
}

export interface InsertCompetitorProductRequest {
  competitor: Competitor;
  products: CompetitorProductRequest[];
}

export interface CompetitorProductSearchResult {
  competitor: Competitor;
  products: CompetitorProductRequest[];
}

export interface ProductData {
  skinSeoulProduct: any;
  machineMatchedProducts: Array<{
    competitor: string;
    competitorProducts: any[];
  }>;
}

export interface MatchedSkinSeoulProduct {
  skinSeoulProduct: {
    sku: string;
    name: string;
    brand: string;
    type: 'Simple' | 'Variable' | 'Variation';
    productLink: string;
    productImageUrls: string[];
    category: string[];
    modifiedAt: string;
  };
  machineMatchedProducts: Array<{
    competitor: string;
    competitorProducts: any[];
  }>;
  humanMatchedProducts: Array<{
    competitor: string;
    competitorProducts: any[];
  }>;
  modifiedAt: string;
}

export interface ListMatchedProductsResponse {
  items: MatchedSkinSeoulProduct[];
  limit: number;
  lastEvaluatedKey?: string | null;
}

export interface FetchMatchedProductsParams {
  limit?: number;
  lastEvaluatedKey?: string;
  search?: string;
  alertType?: 'price_anomaly' | 'size_mismatch';
  noAlerts?: boolean;
  sortBy?: 'sku' | 'brand' | 'name' | 'modifiedAt';
  sortOrder?: 'asc' | 'desc';
}

export interface UnmatchCompetitorRequest {
  skinSeoulSku: string;
  competitor: Competitor;
  competitorSku?: string;
}

export interface ReplaceCompetitorRequest {
  skinSeoulSku: string;
  competitor: Competitor;
  oldCompetitorSku: string;
  newProductLink: string;
}

export interface UpdateNormalizationRequest {
  skinSeoulSku: string;
  competitor: Competitor;
  competitorSku: string;
  overrideRatio?: number;
}

export interface SearchProductsParams {
  q: string;
  page?: number;
  limit?: number;
}

export interface SearchedProduct {
  productId: number;
  sku: string;
  name: string;
  brand: string;
  type: 'Simple' | 'Variable' | 'Variation';
  status: 'instock' | 'outofstock' | 'onbackorder';
  productLink: string;
  productImageUrls: string[];
  category: string[];
}

export interface SearchProductsResponse {
  products: SearchedProduct[];
  total: number;
  page: number;
  limit: number;
}

export interface ListMachineMatchedProductsResponse {
  items: MatchedSkinSeoulProduct[];
  limit?: number;
  lastEvaluatedKey?: string | null;
}

export interface ListMachineUnmatchedProductsResponse {
  items: Array<{
    sku: string;
    name: string;
    brand: string;
    productLink: string;
    productImageUrls: string[];
    category: string[];
    modifiedAt: string;
  }>;
  limit?: number;
  offset?: number;
  total: number;
}

export class CompetitorClient {
  private readonly headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Origin: window.location.origin,
  };

  constructor(private readonly apiUrl: string) {}

  public async fetchMachineMatchedProducts(params?: {
    limit?: number;
    lastEvaluatedKey?: string;
  }): Promise<ListMachineMatchedProductsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);

      const url = queryParams.toString()
        ? `${this.apiUrl}/competitor/machineMatched?${queryParams.toString()}`
        : `${this.apiUrl}/competitor/machineMatched`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch machine matched products: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching machine matched products:', error);
      throw error;
    }
  }

  public async matchCompetitorProduct(
    skinSeoulSku: string,
    humanMatchedProducts: MatchedCompetitorProductsRequest[],
    existingMachineMatchedProducts: MatchedCompetitorProductsRequest[],
  ): Promise<MatchCompetitorResponse> {
    const requestBody: MatchWithSkinSeoulProductRequest = {
      skinSeoulProduct: {
        sku: skinSeoulSku,
        type: SkinSeoulProductType.Simple,
      },
      machineMatchedProducts: existingMachineMatchedProducts || [],
      humanMatchedProducts: humanMatchedProducts || [],
    };

    try {
      const response = await fetch(`${this.apiUrl}/competitor/match`, {
        method: 'POST',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify([requestBody]),
      });

      if (!response.ok) {
        throw new Error(`Failed to match competitor product: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error matching competitor product:', error);
      throw error;
    }
  }

  public async insertCompetitorProduct(request: InsertCompetitorProductRequest): Promise<boolean> {
    try {
      const response = await fetch(`${this.apiUrl}/competitor`, {
        method: 'POST',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to insert competitor product: ${response.statusText}`);
      }

      return true;
    } catch (error) {
      console.error('Error inserting competitor product:', error);
      throw error;
    }
  }

  public async searchCompetitorProducts(competitor: Competitor, term: string): Promise<CompetitorProductSearchResult> {
    try {
      const response = await fetch(
        `${this.apiUrl}/competitor/products/${competitor}/search?term=${encodeURIComponent(term)}`,
        {
          method: 'GET',
          headers: this.headers,
          mode: 'cors',
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to search competitor products: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error searching competitor products:', error);
      throw error;
    }
  }

  public async fetchPaginatedMatchedProducts(
    params: FetchMatchedProductsParams,
  ): Promise<ListMatchedProductsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.set('limit', params.limit.toString());
      if (params.lastEvaluatedKey) queryParams.set('lastEvaluatedKey', params.lastEvaluatedKey);
      if (params.search) queryParams.set('search', params.search);
      if (params.alertType) queryParams.set('alertType', params.alertType);
      if (params.noAlerts !== undefined) queryParams.set('noAlerts', params.noAlerts.toString());
      if (params.sortBy) queryParams.set('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);

      const response = await fetch(`${this.apiUrl}/competitor/matched?${queryParams.toString()}`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch matched products: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching paginated matched products:', error);
      throw error;
    }
  }

  public async unmatchCompetitor(request: UnmatchCompetitorRequest): Promise<MatchCompetitorResponse> {
    try {
      const { skinSeoulSku, competitor, competitorSku } = request;
      const url = `${this.apiUrl}/competitor/match/${encodeURIComponent(skinSeoulSku)}/${encodeURIComponent(competitor)}/${encodeURIComponent(competitorSku || '')}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to unmatch competitor: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error unmatching competitor:', error);
      throw error;
    }
  }

  public async replaceCompetitorUrl(request: ReplaceCompetitorRequest): Promise<MatchCompetitorResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/competitor/match`, {
        method: 'PUT',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to replace competitor URL: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error replacing competitor URL:', error);
      throw error;
    }
  }

  public async updateNormalization(request: UpdateNormalizationRequest): Promise<MatchCompetitorResponse> {
    try {
      const response = await fetch(`${this.apiUrl}/competitor/normalization`, {
        method: 'PUT',
        headers: this.headers,
        mode: 'cors',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to update normalization: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error updating normalization:', error);
      throw error;
    }
  }

  public async searchProducts(params: SearchProductsParams): Promise<SearchProductsResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.set('q', params.q);
      if (params.page) queryParams.set('page', params.page.toString());
      if (params.limit) queryParams.set('limit', params.limit.toString());

      const response = await fetch(`${this.apiUrl}/products/search?${queryParams.toString()}`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to search products: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  public async getMatchedProductsBySkus(skus: string[]): Promise<MatchedSkinSeoulProduct[]> {
    try {
      const queryParams = new URLSearchParams();
      skus.forEach((sku) => queryParams.append('skus', sku));

      const response = await fetch(`${this.apiUrl}/competitor/matchedProducts?${queryParams.toString()}`, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to get matched products by SKUs: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error getting matched products by SKUs:', error);
      throw error;
    }
  }

  public async fetchMachineUnmatchedProducts(params?: {
    limit?: number;
    offset?: number;
  }): Promise<ListMachineUnmatchedProductsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());

      const url = queryParams.toString()
        ? `${this.apiUrl}/competitor/machineUnmatched?${queryParams.toString()}`
        : `${this.apiUrl}/competitor/machineUnmatched`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.headers,
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch machine unmatched products: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Error fetching machine unmatched products:', error);
      throw error;
    }
  }
}
